import { useEffect, useMemo, useRef, useState } from "react";
import { ReactTyped } from "react-typed";
import styled, { keyframes } from "styled-components";
import fastSnap from "../../utils/fastsnap.js";
import buildBelt from "../../utils/conveyor.js";
import spotlight from "../../utils/spotlight.js";

const HeroWrap = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
`;

const fall = keyframes`
  from { transform: translate3d(0, -120vh, 0); }
  to   { transform: translate3d(0, calc(100vh + 160px), 0); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50%       { transform: translateX(-50%) translateY(6px); }
`;

const BeltStage = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;

  .sprite {
    position: absolute;
    top: 0;
    height: 190px;
    will-change: transform, opacity, left, top, height, width;
    filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.25));
    opacity: 0.98;
    animation: ${fall} 36s linear infinite;
    transition:
      opacity 0.2s ease,
      transform 0.3s ease;
  }

  .sprite img {
    display: block;
    height: 100%;
    width: auto;
  }

  &.spotlight .sprite {
    animation-play-state: paused !important;
    opacity: 0.2 !important;
  }

  @media (prefers-reduced-motion: reduce) {
    .sprite {
      animation: none !important;
      transform: translate3d(0, 20vh, 0);
    }
  }
`;

const SpotOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
`;

const SpotAnchor = styled.div`
  position: absolute;
  left: clamp(3rem, 5vw, 10rem);
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 1px;
  pointer-events: none;

  @media (max-width: 900px) and (orientation: portrait) {
    left: max(2vw, env(safe-area-inset-left));
  }
  @media (max-width: 780px) and (orientation: portrait) {
    left: max(0vw, env(safe-area-inset-left));
    top: 40%;
  }
`;

const MainSection = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100vh;
  height: 100svh;
  @supports (height: 100dvh) {
    height: 100dvh;
  }

  display: flex;
  flex-direction: column;

  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.8),
    rgba(255, 255, 255, 0.4)
  );
`;

const MainSlogan = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: min(720px, 58vw);
  padding: 0 2rem;

  @media (max-width: 900px) and (orientation: portrait) {
    max-width: 100%;
    padding: 0 1rem;

    ${MainSection}.spot & {
      align-items: flex-end;
      text-align: right;
      padding-right: 1rem;
      max-width: 92vw;
    }
  }
`;

const Title = styled.div`
  text-align: center;
  font-family: "Ephesis", cursive;
  font-size: min(5rem, 15vw);
  line-height: 1.3;
  min-height: calc(min(5rem, 15vw) * 3 + 15px);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  -webkit-font-smoothing: antialiased;

  .typed-cursor {
    opacity: 0 !important;
    display: none !important;
  }

  @media (max-width: 900px) and (orientation: portrait) {
    ${MainSection}.spot & {
      margin-left: auto;
      text-align: right;
      max-width: 92vw;
    }
  }
`;

const Subtitle = styled.div`
  margin: 10px;
  display: block;
  text-align: center;
  font-size: large;

  @media (max-width: 900px) and (orientation: portrait) {
    font-size: clamp(14px, 3.6vw, 18px);

    ${MainSection}.spot & {
      margin-left: auto;
      margin-right: 0;
      max-width: 36rem;
      width: 92vw;
      padding-right: 1rem;
      text-align: right;
    }
  }

  &.line2 {
    padding-bottom: 5px;
    font-size: x-large;
    @media (max-width: 900px) and (orientation: portrait) {
      font-size: large;
    }
  }

  ${(self) =>
    self.$showNext
      ? `transition: opacity 2s ease, transform 2s ease; opacity:1; transform:translateY(0);`
      : `transition:none; opacity:0; transform:translateY(20px);`}
`;

const ScrollCue = styled.div`
  position: absolute;
  bottom: 3.5rem;
  left: 50%;
  z-index: 5;
  pointer-events: none;
  transition: opacity 1.2s ease;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  animation: ${bounce} 1.6s ease-in-out infinite;
`;

const ChevronArrow = () => (
  <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
    <path
      d="M2 2L10 10L18 2"
      stroke="rgba(35,31,26,0.45)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function WelcomeSection({ onAuthOpen }) {
  const [showNext, setShowNext] = useState(false);
  const [showCue, setShowCue] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowNext(true), 1500);
    const t2 = setTimeout(() => setShowCue(true), 2500);
    const t3 = setTimeout(() => setShowCue(false), 7000);
    const onScroll = () => setShowCue(false);
    window.addEventListener("scroll", onScroll, { passive: true, once: true });
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  const stageRef = useRef(null);
  const overlayRef = useRef(null);
  const sec1Ref = useRef(null);
  const sec2Ref = useRef(null);
  const anchorRef = useRef(null);
  const heroRef = useRef(null);

  const images = useMemo(
    () =>
      Array.from(
        { length: 22 },
        (_, i) => `${process.env.PUBLIC_URL}/assets/people/person${i + 1}.png`,
      ),
    [],
  );

  useEffect(() => {
    if (
      !stageRef.current ||
      !overlayRef.current ||
      !sec1Ref.current ||
      !sec2Ref.current ||
      !anchorRef.current
    )
      return;

    const uninstallSnap = fastSnap(sec1Ref, sec2Ref, heroRef, {
      headerAllowancePx: 10,
      snapBackThreshold: 0.3,
    });
    const uninstallBelt = buildBelt(stageRef.current, images, {
      heightPx: 190,
      laneGapX: 10,
      perLaneMin: 2,
      perLaneMax: 7,
      durationSecs: 36,
    });
    const uninstallSpot = spotlight({
      stageRef,
      overlayRef,
      anchorRef,
      sec1Ref,
      sec2Ref,
    });

    return () => {
      uninstallSnap();
      uninstallBelt();
      uninstallSpot();
    };
  }, [images]);

  return (
    <HeroWrap ref={heroRef}>
      <BeltStage ref={stageRef} />
      <SpotOverlay ref={overlayRef} />
      <MainSection ref={sec1Ref}>
        <MainSlogan>
          <Title style={{ padding: "15px" }}>
            Find Your Next <br />
            <ReactTyped
              strings={[
                "Great Hire",
                "High Performer",
                "Future Leader",
                "Impact Maker",
                "Team Builder",
                "Growth Driver",
                "Competitive Edge",
              ]}
              typeSpeed={50}
              backSpeed={20}
              backDelay={2000}
              loop={true}
              showCursor={false}
            />
          </Title>
          <Subtitle
            $showNext={showNext}
            style={{ paddingTop: "4rem", visibility: "hidden" }}
          >
            Next generation people-ops, built to scale with your team.
          </Subtitle>
          <Subtitle
            $showNext={showNext}
            className="line2"
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            Next generation people-ops, built to scale with your team. <br />
            <br />
            <button onClick={onAuthOpen} style={{ alignSelf: "center" }}>
              Get Free Access
            </button>
          </Subtitle>
        </MainSlogan>
        <ScrollCue $visible={showCue}>
          <ChevronArrow />
        </ScrollCue>
      </MainSection>

      <MainSection ref={sec2Ref} className="spot">
        <MainSlogan>
          <Title style={{ fontFamily: '"Funnel Sans", sans-seri' }}>
            People. Matched. Evolved.
          </Title>
          <Subtitle $showNext className="line2">
            Smarcuit is an intelligent hiring platform built for high growth
            teams. We go beyond the resume by using AI driven matching to
            transform hiring into a precise, data informed science. Instead of
            simply filling roles, Smarcuit helps you build a scalable talent
            network designed to grow with your business, not just fit it.
          </Subtitle>
          <SpotAnchor ref={anchorRef} />
        </MainSlogan>
      </MainSection>
    </HeroWrap>
  );
}

export default WelcomeSection;
